import React, { useCallback, useState, useEffect } from 'react';
import { Icon } from '@shakkerai/components';
import { notifyError } from '@shakkerai/pro-components';
import { translate } from '@shakkerai/util-i18n';
import { pickLocalFiles } from '@shakkerai/util-file';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import { getLoadings, useLoadings } from '@shakkerai/pro-store';
import { useUserInfo, getSharedData, useTempState } from '@starflow/app/store';
import { updateElement } from '@starflow/app/helpers/canvas';
import { showAuthModal } from '@shakkerai/pro-auth/src/utils';
import { uploadImage } from '@shakkerai/pro-utils';
import { Loader } from '@mantine/core';
import opacityBackground from './assets/opacity_background.png';
import { updateUnitData } from '@starflow/app/helpers/flow';
export const MAX_FILE_SIZE = 1024 * 1024 * 20;

const GhostImage = ({ data }) => {
  const { url, top, left } = data;

  return (
    <img
      style={{ top, left }}
      src={url}
      crossOrigin="anonymous"
      className="absolute z-10 -translate-x-1/2 -translate-y-1/2 pointer-events-none w-32 h-32 object-contain opacity-80"
    />
  );
};

export const ImagePreview = ({
  url,
  disabled,
  disabledTip,
  showReload,
  emptyPlaceholder,
  uploadTipTitle,
  uploadTipMessage,
  removeSourceImage,
  originalImageUrl,
  sourceImageOnly,
  ghostImage,
  uploading,
  processing,
  onPickImage,
  onReload,
}) => {
  const isMaskExist = useTempState('isMaskExist');
  if (uploading) {
    return (
      <div className="flex items-center justify-center w-full h-[221px] bg-black/30">
        <Loader size={18} />
      </div>
    );
  }

  if (!url && !originalImageUrl) {
    return (
      <div
        className={`flex flex-col items-center justify-center w-full h-[220px] overflow-hidden bg-[#f4f6f8] ${
          ghostImage ? 'pointer-events-none' : ''
        } relative`}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {emptyPlaceholder || (
            <span className="text-sm text-[#adadad]">{disabledTip || translate('imgupload_placeholder')}</span>
          )}
          <button
            disabled={disabled}
            onClick={onPickImage}
            className="w-32 h-[34px] mt-3.5 bg-white border border-[#ededed] hover:border-[#cdcdcd] rounded text-[#2d2d2d] text-xs font-grotesk"
          >
            {translate('cn_face_upload_image')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-stretch justify-center">
      <div
        className={`flex flex-col items-center justify-center w-[260px] h-[221px] overflow-hidden ${
          ghostImage ? 'pointer-events-none' : ''
        } relative`}
        style={{ backgroundImage: `url(${opacityBackground})` }}
      >
        <img id="color-reference-picture" src={originalImageUrl} className="w-full h-[221px] object-contain" />
        {isMaskExist && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 opacity-0 transition-opacity hover:opacity-100">
            {uploadTipTitle && <h5 className="text-white text-sm font-semibold">{uploadTipTitle}</h5>}
            {uploadTipMessage && (
              <span className="mt-2 mb-4 mx-4 text-xs text-white/80 text-center">{uploadTipMessage}</span>
            )}
            {disabled && disabledTip ? (
              <span className="text-white text-xs font-grotesk font-semibold">{disabledTip}</span>
            ) : (
              <>
                <Icon
                  className="cursor-pointer liblibfont icon-border_close-1 absolute right-2 top-2"
                  fontSize={18}
                  color="#fff"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSourceImage();
                  }}
                />
                <button
                  disabled={disabled}
                  onClick={onPickImage}
                  className="w-[128px] h-[34px] mt-1 border border-white rounded text-white text-xs font-grotesk font-semibold"
                >
                  {translate('imgref_upload')}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      {sourceImageOnly ? null : showReload ? (
        <div className="flex items-center justify-center  bg-[#f1f2f3] w-[260px] h-[221px]">
          <button
            onClick={onReload}
            data-role="btn-reload-cn-image"
            className="w-8 h-8 flex items-center justify-center bg-black/20 rounded-md hover:bg-black/30 transition"
          >
            <Icon className="icon-proreload" fontSize={18} color="#fff" />
          </button>
        </div>
      ) : processing ? (
        <div className="flex flex-1 items-center justify-center h-[221px] bg-black/30">
          <Loader size={18} />
        </div>
      ) : (
        <div
          className={`flex flex-1 flex-col items-center justify-center bg-[#f1f2f3] h-[221px] overflow-hidden pointer-events-none relative`}
        >
          <img key={url || imgCNEmpty} src={url || imgCNEmpty} className="w-full h-[260px] object-contain" />
          <span className="absolute z-[1] top-2 right-2 flex items-center h-[26px] px-3 bg-black/50 rounded text-xs text-white">
            {translate('imgref_preview')}
          </span>
        </div>
      )}
    </div>
  );
};

export const CommonDropZone = React.memo((props) => {
  const {
    disabled,
    disabledTip,
    onImageUpload,
    emptyPlaceholder,
    sourceImage,
    generator,
    uploadingKey = '',
    ...rest
  } = props;
  const { startLoading, stopLoading } = getLoadings();

  const originalImageUrl = convert2CompressedUrl(sourceImage, 400);
  const [ghostImage, setGhostImage] = useState(null);
  const [imageDraggingOver, setImageDraggingOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(originalImageUrl);
  const uploading = useLoadings(`${generator?.id}_commonZone_upload_${uploadingKey}`);

  const uploadSourceImage = useCallback(
    async (file) => {
      if (file.size > MAX_FILE_SIZE) {
        notifyError(translate('common_operation_failed'), translate('img_size_toast'));
        return false;
      }

      const { userInfo } = useUserInfo.getState();
      if (!userInfo) {
        showAuthModal();
        return false;
      }
      startLoading(`${generator?.id}_commonZone_upload_${uploadingKey}`);
      try {
        const result = await uploadImage?.(file);
        setPreviewUrl(result?.url || originalImageUrl);
        onImageUpload?.(result);
      } catch (error) {
        throw error;
      } finally {
        stopLoading(`${generator?.id}_commonZone_upload_${uploadingKey}`);
      }
    },
    [onImageUpload, generator],
  );
  const removeSourceImage = useCallback(
    (e) => {
      setPreviewUrl(null);
      onImageUpload();
    },
    [generator.id],
  );
  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
    setImageDraggingOver(true);
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setImageDraggingOver(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setImageDraggingOver(false);
  }, []);
  // 画布拖动不会执行，从本地文件可以执行
  const handleDrop = (event) => {
    if (disabled) return;

    event.preventDefault();
    setImageDraggingOver(false);

    if (event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      if (file.type.startsWith('image')) {
        uploadSourceImage(file);
      }
    } else {
      // 这里处理生图结果或者生图历史的图片拖拽到参考图
      const draggingImage = getSharedData('dragging-element');

      if (draggingImage) {
        setPreviewUrl(draggingImage?.url);
        onImageUpload?.(ghostImage);
      }
    }
  };

  const handleMouseEnter = useCallback((event) => {
    const draggingElement = getSharedData('dragging-element');

    if (draggingElement) {
      draggingElement.top = event.nativeEvent.offsetY;
      draggingElement.left = event.nativeEvent.offsetX;
      setGhostImage(draggingElement);
      setImageDraggingOver(true);
    }
  }, []);

  const handleMouseMove = useCallback(
    (event) => {
      if (ghostImage) {
        setGhostImage((imageData) =>
          Object.assign({}, imageData, {
            top: event.nativeEvent.offsetY,
            left: event.nativeEvent.offsetX,
          }),
        );
      }
    },
    [ghostImage],
  );

  const handleMouseLeave = useCallback(() => {
    setGhostImage(null);
    setImageDraggingOver(false);
  }, []);

  const handleMouseUp = useCallback(() => {
    if (ghostImage) {
      onImageUpload?.(ghostImage);
      setPreviewUrl(ghostImage?.url);
      // 还原图片位置
      setTimeout(() => {
        updateElement(ghostImage.id, { x: ghostImage.originX, y: ghostImage.originY });
      }, 50);
      // updateElement(ghostImage.id, { x: ghostImage.originX, y: ghostImage.originY });
    }

    setGhostImage(null);
    setImageDraggingOver(false);
  }, [ghostImage]);

  const handlePickLocalImage = useCallback(async () => {
    if (disabled) return;

    const [file] = await pickLocalFiles(['jpeg', 'jpg', 'png', 'webp']);
    if (file) uploadSourceImage(file);
  }, [disabled, uploadSourceImage]);

  useEffect(() => {
    if (generator.data.colorControl.colorPalette.length === 0) {
      removeSourceImage();
    }
  }, [generator.data.colorControl.colorPalette]);
  return (
    <div
      className={`group relative bg-[#f8f9fa] ${ghostImage ? ' cursor-copy' : ''}`}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleMouseUp}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {previewUrl ? (
        <ImagePreview
          url={previewUrl}
          originalImageUrl={previewUrl || originalImageUrl}
          disabled={disabled}
          ghostImage={ghostImage}
          disabledTip={disabledTip}
          highlighted={imageDraggingOver}
          emptyPlaceholder={emptyPlaceholder}
          onPickImage={handlePickLocalImage}
          sourceImageOnly
          uploading={uploading}
          removeSourceImage={removeSourceImage}
          {...rest}
        />
      ) : (
        <div
          className="cursor-pointer bg-[#F4F6F8] text-[#7F7F7F] flex border-[#D6D7DB] justify-center items-center gap-1 h-9 rounded-sm mt-[14px]"
          onClick={handlePickLocalImage}
        >
          <Icon className="shk-shangchuantupian" fontSize={16} color="#7F7F7F" />
          {translate('upload_image_to_get_colors')}
        </div>
      )}

      {!!ghostImage && <GhostImage data={ghostImage} />}
    </div>
  );
});
